import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Copy, Check, Pencil, Plus, Trash2, RotateCcw } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { fillText, myFirstName } from "@/lib/textExamples";

function Highlighted({ text }) {
  return text.split(/(\[[^\]]+\])/g).map((part, i) =>
    part.startsWith("[")
      ? <span key={i} className="px-1 rounded" style={{ background: "color-mix(in srgb, var(--gold) 18%, transparent)", color: "var(--gold)" }}>{part}</span>
      : <span key={i}>{part}</span>
  );
}

function ExampleCard({ n, title, text, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); toast.success("Copied. Fill in the brackets and send."); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Couldn't copy."); }
  };
  return (
    <div className="rounded-md p-4" style={{ background: "var(--bg-base)", border: "1px solid var(--border-subtle)" }} data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-serif-display text-lg leading-none shrink-0" style={{ color: "var(--gold)" }}>{n}</span>
          <h4 className="text-sm font-medium" style={{ color: "var(--ink)" }}>{title}</h4>
        </div>
        <button onClick={copy} className="shrink-0 h-8 px-3 rounded-full flex items-center gap-1.5 text-xs transition-colors hover:bg-[var(--surface-hover)]" style={{ border: "1px solid var(--border-strong)", color: copied ? "var(--olive)" : "var(--ink-2)" }} data-testid={`${testid}-copy`}>
          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "var(--ink-2)" }}><Highlighted text={text} /></p>
    </div>
  );
}

function EditList({ draft, setDraft, footer }) {
  const upd = (i, k, v) => setDraft(draft.map((e, j) => (j === i ? { ...e, [k]: v } : e)));
  return (
    <div className="space-y-3" data-testid="text-examples-edit-list">
      <p className="text-[0.7rem]" style={{ color: "var(--muted)" }}>Use [Name], [Your Name] and [Church] and they'll fill in automatically when someone texts a guest.</p>
      {draft.map((e, i) => (
        <div key={e.id || `new-${i}`} className="rounded-md p-3 space-y-2" style={{ background: "var(--bg-base)", border: "1px solid var(--border-subtle)" }} data-testid={`text-example-edit-${i}`}>
          <div className="flex items-center gap-2">
            <span className="font-serif-display text-lg shrink-0" style={{ color: "var(--gold)" }}>{i + 1}</span>
            <input value={e.title} onChange={(ev) => upd(i, "title", ev.target.value)} placeholder="Title (e.g. After meeting them)" className="flex-1 min-w-0 px-3 py-1.5 text-sm" data-testid={`text-example-edit-title-${i}`} />
            <button onClick={() => setDraft(draft.filter((_, j) => j !== i))} className="p-1.5 shrink-0" style={{ color: "var(--terracotta)" }} title="Delete example" data-testid={`text-example-delete-${i}`}><Trash2 size={14} /></button>
          </div>
          <textarea rows={3} value={e.text} onChange={(ev) => upd(i, "text", ev.target.value)} placeholder="Hey [Name]! This is [Your Name] from [Church]…" className="w-full px-3 py-2 text-sm" data-testid={`text-example-edit-text-${i}`} />
        </div>
      ))}
      <button onClick={() => setDraft([...draft, { id: "", title: "", text: "" }])} className="btn-ghost text-xs" data-testid="text-example-add-btn"><Plus size={13} /> Add example</button>
      {footer}
    </div>
  );
}

export default function TextExamplesModal({ orgId, onClose, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({ "Your Name": myFirstName(user), Church: "" });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/coord/text-examples", { params: { org_id: orgId } })
      .then((r) => { setData(r.data); setVals((v) => ({ ...v, Church: v.Church || r.data.church_name })); })
      .catch(() => toast.error("Couldn't load text examples."));
  }, [orgId]);

  const apply = (r, msg) => { setData(r.data); setDraft(null); onChanged?.(r.data); toast.success(msg); };
  const save = async () => {
    if (draft.some((e) => !e.title.trim() || !e.text.trim())) { toast.error("Every example needs a title and a message."); return; }
    setBusy(true);
    try { apply(await api.put("/coord/text-examples", { examples: draft }, { params: { org_id: orgId } }), "Text examples saved."); }
    catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };
  const restore = async () => {
    if (!window.confirm("Restore the 10 built-in examples? Your church's custom examples will be replaced.")) return;
    setBusy(true);
    try { apply(await api.delete("/coord/text-examples", { params: { org_id: orgId } }), "Built-in examples restored."); }
    catch { toast.error("Couldn't restore."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" style={{ background: "rgba(14,34,64,0.55)" }} onClick={onClose} data-testid="text-examples-overlay">
      <div className="w-full max-w-2xl rounded-md" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} onClick={(e) => e.stopPropagation()} data-testid="text-examples-modal">
        <div className="p-5 flex items-start justify-between gap-3 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <div className="overline" style={{ color: "var(--gold)", fontSize: "0.6rem" }}>Guest follow-up</div>
            <h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>Text examples</h3>
            <p className="text-xs mt-1 max-w-md" style={{ color: "var(--muted)" }}>Friendly ways to reach out. Make them your own: a personal, real message beats a perfect one.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }} aria-label="Close" data-testid="text-examples-close"><X size={18} /></button>
        </div>
        {!data ? <p className="p-5 text-sm" style={{ color: "var(--muted)" }}>Loading…</p> : (
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[0.6rem] uppercase tracking-[0.16em]" style={{ color: data.custom ? "var(--gold)" : "var(--muted)" }} data-testid="text-examples-source">{data.custom ? "Your church's examples" : "Built-in examples"}</span>
              <div className="flex gap-2 flex-wrap">
                {draft ? (
                  <>
                    <button onClick={() => setDraft(null)} className="btn-ghost text-xs" data-testid="text-examples-cancel-btn">Cancel</button>
                    <button onClick={save} disabled={busy} className="btn-primary text-xs" data-testid="text-examples-save-btn">{busy ? "Saving…" : "Save examples"}</button>
                  </>
                ) : (
                  <>
                    {data.custom && <button onClick={restore} disabled={busy} className="btn-ghost text-xs" data-testid="text-examples-restore-btn"><RotateCcw size={13} /> Restore built-in</button>}
                    <button onClick={() => setDraft(data.examples.map((e) => ({ ...e })))} className="btn-ghost text-xs" data-testid="text-examples-edit-btn"><Pencil size={13} /> Edit examples</button>
                  </>
                )}
              </div>
            </div>
            {draft ? <EditList draft={draft} setDraft={setDraft} footer={
              <div className="sticky bottom-0 -mx-5 px-5 py-3 flex justify-end gap-2 border-t" style={{ background: "var(--bg-alt)", borderColor: "var(--border-subtle)" }}>
                <button onClick={() => setDraft(null)} className="btn-ghost text-xs" data-testid="text-examples-cancel-bottom-btn">Cancel</button>
                <button onClick={save} disabled={busy} className="btn-primary text-xs" data-testid="text-examples-save-bottom-btn">{busy ? "Saving…" : "Save examples"}</button>
              </div>
            } /> : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-md" style={{ background: "var(--surface-hover)" }}>
                  {[["Your Name", "text-examples-your-name"], ["Church", "text-examples-church"]].map(([k, tid]) => (
                    <label key={k} className="text-[0.6rem] uppercase tracking-[0.16em]" style={{ color: "var(--muted)" }}>
                      {k}
                      <input value={vals[k]} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} placeholder={k === "Church" ? "Your church" : "Your first name"} className="mt-1 w-full px-3 py-2 text-sm normal-case tracking-normal" data-testid={tid} />
                    </label>
                  ))}
                  <p className="sm:col-span-2 text-[0.7rem]" style={{ color: "var(--muted)" }}>These fill into every example. Tip: tap Text on a guest's row to send one with their name already filled in.</p>
                </div>
                {data.examples.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No examples yet. Tap Edit examples to add one.</p>}
                {data.examples.map((ex, i) => (
                  <ExampleCard key={ex.id || i} n={i + 1} title={ex.title} text={fillText(ex.text, vals)} testid={`text-example-${i + 1}`} />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
